import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { GroupedRoles, RoleType, UpdateRoleForEntityRequest } from '@models/authorization';
import { UserListItemParams, UserListItemResponse, UserResponse, UserStats } from '@models/user';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { NotificationsService } from 'cui-components';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { findAndReplace } from '../helpers/helper';
import { createCustomStateSetOnUrlHandler, createStateResetHandler } from '../helpers/state-reset-handler';
import { getAllWithParams, getOne, StateDictionary } from '../helpers/store-base';
import { UsersService } from './users.service';

type UsersState = {
  users: UserListItemResponse[];
  isLoading: boolean;
  selectedUser: UserResponse | null;
  isSelectedUserLoading: boolean;
  selectedUserRoles: GroupedRoles[];
  isUserRolesLoading: boolean;
  selectedUserStats: UserStats | null;
  isUserStatsLoading: boolean;
  changingAdditionalPartnerListInProgress: boolean;
};

const initialState: UsersState = {
  users: [],
  selectedUser: null,
  selectedUserRoles: [],
  isUserRolesLoading: false,
  selectedUserStats: null,
  isUserStatsLoading: false,
  isSelectedUserLoading: false,
  changingAdditionalPartnerListInProgress: false,
  isLoading: false,
};

export const usersDictionary: StateDictionary<UserListItemResponse> = {
  entityList: 'users',
  listLoading: 'isLoading',
  selectedEntity: 'selectedUser',
  selectedEntityLoading: 'isSelectedUserLoading',
  entityId: 'id',
  createLoading: '',
};

export const UsersStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('users-store'),

  withComputed(state => ({
    selectedUserId: computed(() => state.selectedUser()?.id || null),
  })),

  withMethods(
    (store, usersService = inject(UsersService), router = inject(Router), notificationsService = inject(NotificationsService)) => {
      createStateResetHandler<UsersState>('/users')(store, initialState);
      createCustomStateSetOnUrlHandler<UsersState>('/users', { selectedUser: null })(store);

      const repositoryMethods = {
        getAll: getAllWithParams<UserListItemResponse, Partial<UserListItemParams>>({
          store,
          service: usersService,
          dictionary: usersDictionary,
        }),

        getOne: getOne<UserResponse>({
          store,
          service: usersService,
          dictionary: usersDictionary,
        }),

        getUserRoles: rxMethod<{ id: string; partnerId: string; filter: RoleType }>(
          pipe(
            tap(() => patchState(store, { selectedUserRoles: [], isUserRolesLoading: true })),
            switchMap(({ id, partnerId, filter }) =>
              usersService.getUserRoles(id, partnerId, filter).pipe(
                tap(roles => patchState(store, { selectedUserRoles: roles, isUserRolesLoading: false })),
                catchError(() => {
                  patchState(store, { isUserRolesLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        getUserStats: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { isUserStatsLoading: true })),
            switchMap(id =>
              usersService.getUserStats(id).pipe(
                tap(stats => patchState(store, { selectedUserStats: stats, isUserStatsLoading: false })),
                catchError(() => {
                  patchState(store, { isUserStatsLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        updateUserRoles: rxMethod<{
          updatedRoles: UpdateRoleForEntityRequest[];
          forUserId: string;
          forPartnerId: string;
          filter: RoleType;
        }>(
          pipe(
            tap(() => patchState(store, { isUserRolesLoading: true })),
            switchMap(({ updatedRoles, forUserId, forPartnerId, filter }) =>
              usersService.setUserRoles(forUserId, forPartnerId, updatedRoles, filter).pipe(
                tap(roles => {
                  const urlMatchesType =
                    (filter === 'Internal' && router.url.includes('internal-roles')) ||
                    (filter === 'Regular' && !router.url.includes('internal-roles'));
                  if (urlMatchesType) {
                    patchState(store, { selectedUserRoles: roles, isUserRolesLoading: false });
                  }
                }),
                catchError(() => {
                  patchState(store, { isUserRolesLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        addAdditionalPartnerForUser: rxMethod<{ userId: string; additionalPartnerId: string }>(
          pipe(
            tap(() => patchState(store, { isSelectedUserLoading: true, changingAdditionalPartnerListInProgress: true })),
            switchMap(({ userId, additionalPartnerId }) =>
              usersService.addAdditionalPartner(userId, additionalPartnerId).pipe(
                tap(user =>
                  patchState(store, { isSelectedUserLoading: false, selectedUser: user, changingAdditionalPartnerListInProgress: false }),
                ),
                catchError(() => {
                  patchState(store, { isSelectedUserLoading: false, changingAdditionalPartnerListInProgress: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        removeAdditionalPartnerFromUser: rxMethod<{ userId: string; additionalPartnerId: string }>(
          pipe(
            tap(() => patchState(store, { isSelectedUserLoading: true, changingAdditionalPartnerListInProgress: true })),
            switchMap(({ userId, additionalPartnerId }) =>
              usersService.removeAdditionalPartner(userId, additionalPartnerId).pipe(
                tap(user =>
                  patchState(store, { isSelectedUserLoading: false, selectedUser: user, changingAdditionalPartnerListInProgress: false }),
                ),
                catchError(() => {
                  patchState(store, { isSelectedUserLoading: false, changingAdditionalPartnerListInProgress: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        blockUnblockUser: rxMethod<{ id: string; block: boolean }>(
          pipe(
            tap(() => patchState(store, { isLoading: true, isSelectedUserLoading: true })),
            switchMap(({ id, block }) =>
              usersService.blockUnblockUser(id, block).pipe(
                tap(user =>
                  patchState(store, state => ({
                    selectedUser: user,
                    users: findAndReplace(state.users, user, u => u.id === user.id),
                    isLoading: false,
                    isSelectedUserLoading: false,
                  })),
                ),
                catchError(() => {
                  patchState(store, { isLoading: false, isSelectedUserLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        markForDeletion: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { isLoading: true, isSelectedUserLoading: true })),
            switchMap(id =>
              usersService.markForDeletion(id).pipe(
                tap(user =>
                  patchState(store, state => ({
                    selectedUser: user,
                    users: findAndReplace(state.users, user, u => u.id === user.id),
                    isLoading: false,
                    isSelectedUserLoading: false,
                  })),
                ),
                catchError(() => {
                  patchState(store, { isLoading: false, isSelectedUserLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        updateCrmAccId: rxMethod<{ userId: string; crmContactId: string }>(
          pipe(
            tap(() => patchState(store, { isLoading: true, isSelectedUserLoading: true })),
            switchMap(({ userId, crmContactId }) =>
              usersService.updateCrmContactId(userId, crmContactId).pipe(
                tap(user => patchState(store, { selectedUser: user, isLoading: false, isSelectedUserLoading: false })),
                catchError(() => {
                  patchState(store, { isLoading: false, isSelectedUserLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        resetUserTwoFaById: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { isLoading: true, isSelectedUserLoading: true })),
            switchMap(userId =>
              usersService.resetUserTwoFa(userId).pipe(
                tap(() => {
                  notificationsService.showNotification('2FA has been reset successfully.');
                  patchState(store, { isLoading: false, isSelectedUserLoading: false });
                }),
                catchError(() => {
                  patchState(store, { isLoading: false, isSelectedUserLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        downloadPermissions: rxMethod<{ id: string; partnerId: string; email: string }>(
          pipe(
            switchMap(({ id, partnerId, email }) =>
              usersService.getUserPermissions(id, partnerId).pipe(
                tap(permissions => {
                  const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(permissions, null, 2));
                  const downloadAnchorNode = document.createElement('a');
                  downloadAnchorNode.setAttribute('href', dataStr);
                  downloadAnchorNode.setAttribute('download', `${email}-permissions.json`);
                  document.body.appendChild(downloadAnchorNode);
                  downloadAnchorNode.click();
                  downloadAnchorNode.remove();
                }),
                catchError(() => EMPTY),
              ),
            ),
          ),
        ),
      };

      const navigationMethods = {
        goToUserActions: async (userId: string): Promise<boolean> => {
          const result = await router.navigate(['users', userId]);
          if (!result) return false;

          return true;
        },
      };

      const resetState = () => patchState(store, initialState);

      return {
        ...repositoryMethods,
        ...navigationMethods,
        resetState,
      };
    },
  ),
);

export interface UsersStoreType {
  readonly users: Signal<UserListItemResponse[]>;
  readonly selectedUser: Signal<UserResponse | null>;
  readonly isSelectedUserLoading: Signal<boolean>;
  readonly selectedUserRoles: Signal<GroupedRoles[]>;
  readonly isUserRolesLoading: Signal<boolean>;
  readonly selectedUserStats: Signal<UserStats | null>;
  readonly isUserStatsLoading: Signal<boolean>;
  readonly changingAdditionalPartnerListInProgress: Signal<boolean>;
  readonly isLoading: Signal<boolean>;

  readonly selectedUserId: Signal<string | null>;

  readonly getAll: (query: Partial<UserListItemParams>) => void;
  readonly getOne: (userId: string) => void;
  readonly getUserRoles: (input: { id: string; partnerId: string; filter: RoleType }) => void;
  readonly getUserStats: (id: string) => void;
  readonly updateUserRoles: (input: {
    updatedRoles: UpdateRoleForEntityRequest[];
    forUserId: string;
    forPartnerId: string;
    filter: RoleType;
  }) => void;
  readonly addAdditionalPartnerForUser: (input: { userId: string; additionalPartnerId: string }) => void;
  readonly removeAdditionalPartnerFromUser: (input: { userId: string; additionalPartnerId: string }) => void;
  readonly blockUnblockUser: (input: { id: string; block: boolean }) => void;
  readonly markForDeletion: (id: string) => void;
  readonly updateCrmAccId: (input: { userId: string; crmContactId: string }) => void;
  readonly resetUserTwoFaById: (userId: string) => void;
  readonly downloadPermissions: (input: { id: string; partnerId: string; email: string }) => void;
  readonly goToUserActions: (userId: string) => Promise<boolean>;
  readonly resetState: () => void;
}
